import React, { useState } from "react"

import { useRecoilValue } from "recoil"

import { calculatedUtilityRateState } from "~/containers/Proposal/state/utility-costs-and-savings/utility-costs-and-savings-state"
import { TextInput } from "~/ui/lib/input/TextInput"

import {
  getSecondaryButtonStyles,
  getPrimaryButtonStyles,
} from "./shared/button-styles"
import type { BillEntryData } from "./types"
import { UtilityRateEditor } from "./UtilityRateEditor"

interface BillEntryFormProps {
  onSubmit: (data: BillEntryData) => void
  onCancel: () => void
  isLoading?: boolean
  canEdit?: boolean
}

export const BillEntryForm: React.FC<BillEntryFormProps> = ({
  onSubmit,
  onCancel,
  isLoading = false,
  canEdit = true,
}) => {
  const [billAmount, setBillAmount] = useState(``)
  const [startDate, setStartDate] = useState(``)
  const [endDate, setEndDate] = useState(``)
  const utilityRate = useRecoilValue(calculatedUtilityRateState)

  const handleSubmit = () => {
    if (!canEdit) return

    if (isValid) {
      onSubmit({ billAmount, startDate, endDate })
    }
  }

  const isValid =
    billAmount &&
    parseFloat(billAmount) > 0 &&
    startDate &&
    endDate &&
    utilityRate > 0

  return (
    <div className="w-full">
      <h3
        className="text-base font-bold leading-[22px] tracking-[0.75px] capitalize text-[var(--blue-dark)]"
        style={{ marginBottom: `24px`, fontSize: `18px` }}
      >
        Start from a Bill
      </h3>

      <UtilityRateEditor className="mb-6" canEdit={canEdit} />

      <div className="mb-6">
        <TextInput
          id="bill-amount"
          label="Bill Amount ($)"
          type="number"
          value={billAmount}
          onChange={(e) => setBillAmount(e.target.value)}
          placeholder="441"
          disabled={isLoading || !canEdit}
        />
      </div>

      <span className="flex flex-col justify-between items-start gap-[30px] min-h-[55px] relative min-[750px]:flex-row min-[750px]:gap-y-0 [&>div]:w-full [&>div]:overflow-hidden mb-8">
        <TextInput
          id="start-date"
          label="Billing Period Start Date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          disabled={isLoading || !canEdit}
        />
        <TextInput
          id="end-date"
          label="Billing Period End Date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          disabled={isLoading || !canEdit}
        />
      </span>

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading || !canEdit}
          style={getSecondaryButtonStyles(isLoading || !canEdit)}
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!isValid || isLoading || !canEdit}
          style={getPrimaryButtonStyles(!isValid || isLoading || !canEdit)}
        >
          {isLoading ? `Generating Profile...` : `Generate Profile`}
        </button>
      </div>
    </div>
  )
}
